/**
 * @file Formulario dinámico desde `structure.json` (image, form, text, list).
 * @module formController
 */

import { pickLocalized } from "./i18n.js";
import { openSquareImageCrop } from "./imageSquareCrop.js";

/** Placeholder SVG; `readData` ignora si sigue activo. */
const PROFILE_PHOTO_PLACEHOLDER_SRC = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
	`<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120" aria-hidden="true">
  <circle cx="60" cy="60" r="60" fill="#2a2a2a"/>
  <circle cx="60" cy="48" r="16" fill="none" stroke="#6b7280" stroke-width="2.5"/>
  <path d="M36 98c0-14 10-24 24-24s24 10 24 24" fill="none" stroke="#6b7280" stroke-width="2.5" stroke-linecap="round"/>
</svg>`
)}`;

/** @param {HTMLImageElement} thumb */
function setProfilePhotoPlaceholder(thumb) {
	thumb.src = PROFILE_PHOTO_PLACEHOLDER_SRC;
	thumb.dataset.profilePlaceholder = "1";
	thumb.classList.add("profile-thumb--placeholder");
}

/** @param {HTMLImageElement} thumb @param {string} dataUrl */
function setProfilePhotoUserImage(thumb, dataUrl) {
	thumb.src = dataUrl;
	delete thumb.dataset.profilePlaceholder;
	thumb.classList.remove("profile-thumb--placeholder");
}

/** @param {HTMLImageElement | null | undefined} thumb */
function profileThumbHasUserImage(thumb) {
	return Boolean(thumb && thumb.dataset.profilePlaceholder !== "1");
}

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {Object} [attrs]
 */
function createEl(tag, className, attrs = {}) {
	const el = document.createElement(tag);
	if (className) el.className = className;
	Object.entries(attrs).forEach(([k, v]) => {
		if (v !== undefined && v !== null) el.setAttribute(k, String(v));
	});
	return el;
}

/**
 * Texto de ayuda opcional bajo el título de sección (misma forma que `title`: `{ en, es }` o string).
 * @param {HTMLElement} fieldset
 * @param {Record<string, unknown>} section
 * @param {"en" | "es"} locale
 */
function appendSectionInstructions(fieldset, section, locale) {
	if (!section.instructions) return;
	const text = pickLocalized(section.instructions, locale).trim();
	if (!text) return;
	const p = createEl("p", "form-section-instructions");
	p.textContent = text;
	fieldset.append(p);
}

/**
 * Muestra u oculta Quitar / Recortar según haya foto de usuario.
 * @param {HTMLElement | null | undefined} fieldset
 */
function syncProfilePhotoActions(fieldset) {
	const thumb = fieldset?.querySelector("img.profile-thumb");
	const actions = fieldset?.querySelector(".profile-photo-actions");
	if (!thumb || !actions) return;
	actions.hidden = !profileThumbHasUserImage(thumb);
}

/**
 * @param {Record<string, unknown>} field
 * @param {"en" | "es"} locale
 * @param {() => void} onUpdate
 */
function buildCheckboxGroup(field, locale, onUpdate) {
	const wrap = createEl("div", "form-field form-field--checkboxes");
	wrap.setAttribute("data-checkbox-for", String(field.id ?? ""));
	const heading = createEl("div", "form-field__legend");
	heading.textContent = pickLocalized(field.label, locale);
	wrap.append(heading);
	const list = createEl("div", "form-field__checkbox-list");
	const options = Array.isArray(field.options) ? field.options : [];
	for (const opt of options) {
		const row = createEl("label", "form-checkbox-row");
		const cb = document.createElement("input");
		cb.type = "checkbox";
		cb.className = "form-checkbox-row__input";
		cb.dataset.fieldId = field.id;
		cb.dataset.optionValue = String(opt.value ?? opt.id ?? "");
		cb.addEventListener("change", () => {
			wrap.classList.remove("has-error");
			onUpdate();
		});
		const span = createEl("span", "form-checkbox-row__text");
		span.textContent = pickLocalized(opt.label, locale);
		row.append(cb, span);
		list.append(row);
	}
	wrap.append(list);
	if (field.required === true) {
		wrap.classList.add("form-field--required");
		heading.classList.add("form-field__legend--required");
		wrap.setAttribute("role", "group");
		wrap.setAttribute("aria-required", "true");
	}
	return wrap;
}

/**
 * @param {Record<string, unknown>} field
 * @param {"en" | "es"} locale
 */
function buildTextInput(field, locale) {
	if (field.inputType === "title") {
		const wrap = createEl("div", "form-field form-field--title form-floating");
		const control = createEl("input");
		control.type = "text";
		control.className = "form-control form-control--title";
		control.id = field.id;
		control.placeholder = " ";
		control.dataset.fieldId = field.id;
		const label = createEl("label");
		label.htmlFor = field.id;
		label.textContent = pickLocalized(field.label, locale);
		const clearFieldError = () => wrap.classList.remove("has-error");
		control.addEventListener("input", clearFieldError);
		control.addEventListener("change", clearFieldError);
		wrap.append(control, label);
		if (field.required === true) {
			wrap.classList.add("form-field--required");
			control.setAttribute("aria-required", "true");
		}
		return wrap;
	}

	const wrap = createEl("div", "form-field form-floating");
	const isTa = field.inputType === "textarea";
	const control = isTa ? createEl("textarea") : createEl("input");
	if (control.tagName === "INPUT") {
		control.type = field.inputType || "text";
	}
	control.className = isTa
		? "form-control form-control--textarea"
		: "form-control";
	control.id = field.id;
	control.placeholder = " ";
	control.dataset.fieldId = field.id;
	const label = createEl("label");
	label.htmlFor = field.id;
	label.textContent = pickLocalized(field.label, locale);
	const clearFieldErrorFloating = () => wrap.classList.remove("has-error");
	control.addEventListener("input", clearFieldErrorFloating);
	control.addEventListener("change", clearFieldErrorFloating);
	wrap.append(control, label);
	if (field.required === true) {
		wrap.classList.add("form-field--required");
		control.setAttribute("aria-required", "true");
	}
	return wrap;
}

/**
 * @param {HTMLElement} rowsHost
 * @param {Record<string, unknown>} section
 * @param {() => void} onUpdate
 * @param {"en" | "es"} locale
 * @param {object} messages Textos de lista y recorte (mismo shape que `mountCvForm`).
 */
function appendListAddFooter(rowsHost, section, onUpdate, locale, messages) {
	const existing = rowsHost.querySelector(".list-rows__add");
	if (existing) existing.remove();
	const wrap = createEl("div", "list-rows__add");
	const btn = createEl("button", "list-rows__add-btn", { type: "button" });
	btn.setAttribute("aria-label", messages.addListRow);
	btn.textContent = "+";
	btn.addEventListener("click", () => {
		const newRow = buildListRow(
			section,
			{},
			onUpdate,
			locale,
			messages,
			true
		);
		wrap.before(newRow);
		rowsHost.closest("fieldset")?.classList.remove("has-error");
		onUpdate();
	});
	wrap.append(btn);
	rowsHost.append(wrap);
}

/**
 * @param {Record<string, unknown>} section
 * @param {Record<string, unknown>} rowValues
 * @param {() => void} onUpdate
 * @param {"en" | "es"} locale
 * @param {object} messages
 * @param {boolean} [withRemove]
 */
function buildListRow(
	section,
	rowValues,
	onUpdate,
	locale,
	messages,
	withRemove = false
) {
	const row = createEl("div", "list-row");
	const inner = createEl("div", "list-row__inner");
	const gridClass =
		typeof section.listRowGridClass === "string" && section.listRowGridClass
			? section.listRowGridClass
			: "form-grid";
	const grid = createEl("div", gridClass);
	for (const field of section.itemFields) {
		const uid = `${section.id}-${field.id}-${Math.random().toString(36).slice(2, 9)}`;
		const isTa = field.inputType === "textarea";
		const wrap = createEl("div", "form-field form-floating");
		const control = isTa ? createEl("textarea") : createEl("input");
		if (control.tagName === "INPUT") {
			control.type = field.inputType || "text";
		}
		control.className = isTa
			? "form-control form-control--textarea"
			: "form-control";
		control.id = uid;
		control.placeholder = " ";
		control.dataset.fieldId = field.id;
		control.value = rowValues[field.id] ?? "";
		const label = createEl("label");
		label.htmlFor = uid;
		label.textContent = pickLocalized(field.label, locale);
		const fire = () => onUpdate();
		const clearListFieldError = () => {
			wrap.classList.remove("has-error");
			row.closest("fieldset")?.classList.remove("has-error");
		};
		control.addEventListener("keyup", fire);
		control.addEventListener("change", fire);
		control.addEventListener("input", clearListFieldError);
		control.addEventListener("change", clearListFieldError);
		if (field.required === true) {
			wrap.classList.add("form-field--required");
			control.setAttribute("aria-required", "true");
		}
		wrap.append(control, label);
		grid.append(wrap);
	}
	if (withRemove) {
		const actions = createEl("div", "list-row__actions");
		const removeBtn = createEl("button", "list-row__remove", {
			type: "button",
		});
		removeBtn.setAttribute("aria-label", messages.removeListRow);
		removeBtn.textContent = "−";
		removeBtn.addEventListener("click", (e) => {
			e.preventDefault();
			const host = row.closest(".list-rows");
			if (!host) return;
			const rows = [...host.querySelectorAll(".list-row")];
			const idx = rows.indexOf(row);
			if (idx <= 0 || rows.length <= 1) return;
			row.remove();
			onUpdate();
		});
		actions.append(removeBtn);
		inner.append(grid, actions);
	} else {
		inner.append(grid);
	}
	row.append(inner);
	return row;
}

/**
 * @param {{
 *   container: HTMLElement,
 *   structure: object,
 *   onUpdate: () => void,
 *   locale: "en" | "es",
 *   messages: {
 *     uploadImage: string,
 *     removeProfileImage: string,
 *     recropProfileImage: string,
 *     addListRow: string,
 *     removeListRow: string,
 *     crop: { title: string, zoom: string, hint: string, apply: string, cancel: string }
 *   }
 * }} options
 * @returns {{
 *   readData: () => object,
 *   applyData: (data: object | undefined) => void,
 * }}
 */
export function mountCvForm({ container, structure, onUpdate, locale, messages }) {
	container.innerHTML = "";

	const fire = () => onUpdate();

	for (const section of structure.sections) {
		const fieldset = createEl("fieldset");
		fieldset.dataset.sectionId = section.id;
		const legend = createEl("legend");
		legend.textContent = pickLocalized(section.title, locale);
		fieldset.append(legend);
		appendSectionInstructions(fieldset, section, locale);

		if (section.type === "image") {
			fieldset.dataset.sectionType = "image";
			const wrap = createEl("div", "form-field profile-photo-field");
			const label = createEl("label");
			label.textContent = messages.uploadImage;
			const input = createEl("input");
			input.type = "file";
			input.accept = "image/*";
			input.dataset.imageFieldKey = section.fieldKey;
			const preview = createEl("div", "profile-photo-preview");
			const thumb = createEl("img", "profile-thumb");
			thumb.alt = "";
			setProfilePhotoPlaceholder(thumb);
			preview.append(thumb);
			const actions = createEl("div", "profile-photo-actions");
			actions.hidden = true;
			const btnRemove = createEl(
				"button",
				"app-button app-button--secondary app-button--sm",
				{ type: "button" }
			);
			btnRemove.textContent = messages.removeProfileImage;
			btnRemove.addEventListener("click", (e) => {
				e.preventDefault();
				input.value = "";
				setProfilePhotoPlaceholder(thumb);
				syncProfilePhotoActions(fieldset);
				fire();
			});
			const btnRecrop = createEl(
				"button",
				"app-button app-button--secondary app-button--sm",
				{ type: "button" }
			);
			btnRecrop.textContent = messages.recropProfileImage;
			btnRecrop.addEventListener("click", async (e) => {
				e.preventDefault();
				if (!profileThumbHasUserImage(thumb)) return;
				const src = thumb.getAttribute("src");
				if (!src || !src.startsWith("data:")) return;
				const cropped = await openSquareImageCrop(src, messages.crop);
				if (!cropped) return;
				setProfilePhotoUserImage(thumb, cropped);
				fire();
			});
			actions.append(btnRemove, btnRecrop);
			const side = createEl("div", "profile-photo-side");
			side.append(label, input, actions);
			input.addEventListener("change", () => {
				const file = input.files && input.files[0];
				if (!file) {
					setProfilePhotoPlaceholder(thumb);
					syncProfilePhotoActions(fieldset);
					fire();
					return;
				}
				const reader = new FileReader();
				reader.onload = async () => {
					const raw = String(reader.result);
					const cropped = await openSquareImageCrop(raw, messages.crop);
					if (!cropped) {
						input.value = "";
						setProfilePhotoPlaceholder(thumb);
						syncProfilePhotoActions(fieldset);
						fire();
						return;
					}
					setProfilePhotoUserImage(thumb, cropped);
					syncProfilePhotoActions(fieldset);
					fire();
				};
				reader.readAsDataURL(file);
			});
			wrap.append(preview, side);
			fieldset.append(wrap);
			syncProfilePhotoActions(fieldset);
		} else if (section.type === "form") {
			fieldset.dataset.sectionType = "form";
			const gridClass =
				typeof section.formGridClass === "string" && section.formGridClass
					? section.formGridClass
					: "form-grid";
			const grid = createEl("div", gridClass);
			for (const field of section.fields) {
				if (field.inputType === "checkboxes") {
					grid.append(buildCheckboxGroup(field, locale, fire));
				} else {
					const wrap = buildTextInput(field, locale);
					const input = wrap.querySelector("input, textarea");
					if (input) {
						input.addEventListener("keyup", fire);
						input.addEventListener("change", fire);
					}
					grid.append(wrap);
				}
			}
			fieldset.append(grid);
		} else if (section.type === "text") {
			fieldset.dataset.sectionType = "text";
			const wrap = createEl("div", "form-field form-floating");
			const taId = `fld-text-${section.fieldKey}`;
			const ta = createEl("textarea");
			ta.className = "form-control form-control--textarea form-control--tall";
			ta.id = taId;
			ta.placeholder = " ";
			ta.dataset.textFieldKey = section.fieldKey;
			ta.rows = 5;
			ta.addEventListener("keyup", fire);
			ta.addEventListener("change", fire);
			const label = createEl("label");
			label.htmlFor = taId;
			label.textContent = pickLocalized(section.title, locale);
			wrap.append(ta, label);
			fieldset.append(wrap);
		} else if (section.type === "list") {
			fieldset.dataset.sectionType = "list";
			const rowsHost = createEl("div", "list-rows");
			rowsHost.append(
				buildListRow(section, {}, onUpdate, locale, messages, false)
			);
			appendListAddFooter(rowsHost, section, onUpdate, locale, messages);
			fieldset.append(rowsHost);
		}

		container.append(fieldset);
	}

	return {
		readData() {
			return readFormData(container, structure);
		},
		applyData(data) {
			applyFormData(container, structure, data, onUpdate, locale, messages);
		},
	};
}

/**
 * @param {HTMLElement} container
 * @param {Record<string, unknown>} structure
 * @returns {Record<string, unknown>}
 */
function readFormData(container, structure) {
	const out = {};

	for (const section of structure.sections) {
		const fieldset = container.querySelector(
			`fieldset[data-section-id="${section.id}"]`
		);
		if (section.type === "image") {
			const img = fieldset?.querySelector("img.profile-thumb");
			if (
				img &&
				profileThumbHasUserImage(img) &&
				typeof img.src === "string" &&
				img.src.startsWith("data:")
			) {
				out[section.fieldKey] = img.src;
			}
		} else if (section.type === "form") {
			for (const field of section.fields) {
				if (field.inputType === "checkboxes") {
					const els = fieldset?.querySelectorAll(
						`input[type="checkbox"][data-field-id="${field.id}"]:checked`
					);
					out[field.id] = [...(els ?? [])].map(
						(el) => el.dataset.optionValue ?? ""
					);
				} else {
					const el = fieldset?.querySelector(`[data-field-id="${field.id}"]`);
					if (el) out[field.id] = el.value.trim();
				}
			}
		} else if (section.type === "text") {
			const el = fieldset?.querySelector(
				`textarea[data-text-field-key="${section.fieldKey}"]`
			);
			if (el) out[section.fieldKey] = el.value.trim();
		} else if (section.type === "list") {
			if (!fieldset) {
				out[section.id] = [];
				continue;
			}
			const rows = fieldset.querySelectorAll(".list-row");
			const items = [];
			for (const row of rows) {
				const item = {};
				for (const field of section.itemFields) {
					const input = row.querySelector(`[data-field-id="${field.id}"]`);
					if (input) item[field.id] = input.value.trim();
				}
				const nonEmpty = Object.values(item).some((v) => v.length > 0);
				if (nonEmpty) items.push(item);
			}
			out[section.id] = items;
		}
	}

	return out;
}

/**
 * @param {HTMLElement} container
 * @param {Record<string, unknown>} structure
 * @param {Record<string, unknown> | null | undefined} data
 * @param {() => void} onUpdate
 * @param {"en" | "es"} locale
 * @param {object} messages Textos UI (mismo shape que `mountCvForm`).
 */
function applyFormData(
	container,
	structure,
	data,
	onUpdate,
	locale,
	messages
) {
	if (!data || typeof data !== "object") return;

	for (const section of structure.sections) {
		const fieldset = container.querySelector(
			`fieldset[data-section-id="${section.id}"]`
		);
		if (section.type === "image") {
			const key = section.fieldKey;
			const thumb = fieldset?.querySelector("img.profile-thumb");
			const input = fieldset?.querySelector(
				`input[data-image-field-key="${section.fieldKey}"]`
			);
			if (typeof data[key] === "string" && data[key].startsWith("data:")) {
				if (thumb) setProfilePhotoUserImage(thumb, data[key]);
			} else {
				if (thumb) setProfilePhotoPlaceholder(thumb);
				if (input) input.value = "";
			}
			syncProfilePhotoActions(fieldset);
		} else if (section.type === "form") {
			for (const field of section.fields) {
				if (field.inputType === "checkboxes") {
					const selected = new Set(
						(Array.isArray(data[field.id]) ? data[field.id] : []).map(String)
					);
					fieldset
						?.querySelectorAll(
							`input[type="checkbox"][data-field-id="${field.id}"]`
						)
						.forEach((el) => {
							el.checked = selected.has(String(el.dataset.optionValue ?? ""));
						});
				} else {
					const el = fieldset?.querySelector(`[data-field-id="${field.id}"]`);
					if (el && data[field.id] !== undefined) el.value = data[field.id];
				}
			}
		} else if (section.type === "text") {
			const el = fieldset?.querySelector(
				`textarea[data-text-field-key="${section.fieldKey}"]`
			);
			if (el && data[section.fieldKey] !== undefined) {
				el.value = data[section.fieldKey];
			}
		} else if (section.type === "list") {
			if (!fieldset) continue;
			const rowsHost = fieldset.querySelector(".list-rows");
			if (!rowsHost) continue;
			rowsHost.innerHTML = "";
			const arr = Array.isArray(data[section.id]) ? data[section.id] : [];
			const rowsData = arr.length > 0 ? arr : [{}];
			rowsData.forEach((rowValues, i) => {
				rowsHost.append(
					buildListRow(
						section,
						rowValues,
						onUpdate,
						locale,
						messages,
						i > 0
					)
				);
			});
			appendListAddFooter(rowsHost, section, onUpdate, locale, messages);
		}
	}

	onUpdate();
}